/**
 * Lane-packing algoritmus pro jeden týdenní řádek měsíčního kalendáře.
 *
 * Eventy protínající týden se klipnou na sloupce Po–Ne (0–6), seřadí deterministicky
 * (startCol vzestupně, šířka sestupně, název/id jako tiebreak) a greedy se naskládají
 * do nejnižší volné lane.
 */
import { addDays, differenceInCalendarDays } from 'date-fns';

export interface LaneItem {
  termId: string;
  name: string;
  /** První den (lokální půlnoc) */
  start: Date;
  /** Poslední den, inkluzivně (lokální půlnoc) */
  end: Date;
}

export interface WeekSegment<T extends LaneItem = LaneItem> {
  item: T;
  /** Sloupec začátku 0–6 (pondělí = 0) */
  startCol: number;
  /** Počet obsazených sloupců 1–7 */
  span: number;
  /** Pokračuje z předchozího týdne */
  clippedStart: boolean;
  /** Pokračuje do dalšího týdne */
  clippedEnd: boolean;
  /** Přiřazená lane (0 = první řádek pod čísly dnů) */
  lane: number;
}

export function layoutWeek<T extends LaneItem>(
  items: readonly T[],
  weekStart: Date
): { segments: WeekSegment<T>[]; laneCount: number } {
  const weekEnd = addDays(weekStart, 6);

  const segments: WeekSegment<T>[] = items
    .filter((item) => item.start <= weekEnd && item.end >= weekStart)
    .map((item) => {
      const startCol = Math.max(0, differenceInCalendarDays(item.start, weekStart));
      const endCol = Math.min(6, differenceInCalendarDays(item.end, weekStart));
      return {
        item,
        startCol,
        span: endCol - startCol + 1,
        clippedStart: item.start < weekStart,
        clippedEnd: item.end > weekEnd,
        lane: 0,
      };
    });

  segments.sort(
    (a, b) =>
      a.startCol - b.startCol ||
      b.span - a.span ||
      a.item.name.localeCompare(b.item.name) ||
      a.item.termId.localeCompare(b.item.termId)
  );

  // Greedy packing: laneEnds[lane] = poslední obsazený sloupec dané lane
  const laneEnds: number[] = [];
  for (const segment of segments) {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane]! >= segment.startCol) {
      lane += 1;
    }
    segment.lane = lane;
    laneEnds[lane] = segment.startCol + segment.span - 1;
  }

  return { segments, laneCount: laneEnds.length };
}
